/**
 * Leaf module: canonical deep Zod schemas for Salesforce campaign/member reads
 * and the signal-action read envelopes.
 *
 * IMPORT CONSTRAINT: This file MUST import ONLY from `zod` — no other project
 * imports. It is a pure leaf so the native reads under `reads/` and the
 * launcher sync services can both import it without creating a cycle
 * (`launcher-*-sync.ts` -> `sfdc-users.ts` -> `factories.ts` would otherwise
 * loop back through anything that imports the sync services).
 *
 * Shape invariants (do NOT loosen — these guard NOT NULL DB columns):
 *   - `id`   required (z.string()) on campaign + member — never z.string().nullish()
 *   - `name` required (z.string()) on campaign — launcher_sfdc_campaigns.name is TEXT NOT NULL
 *   - All other campaign/member fields use .nullish() (Middleman omits unset SFDC fields)
 *   - `memberCount` uses .default(0) (campaign rollup may be absent; launcher coalesced with ?? 0)
 *   - `.passthrough()` on nested contact/lead/account schemas (unknown fields survive)
 */
import { z } from 'zod';

export const SalesforceIdRowSchema = z.object({ Id: z.string() }).loose();

// ─── Campaign ────────────────────────────────────────────────────────────────

/**
 * Deep schema for a single Salesforce Campaign returned by
 * POST /salesforce/campaigns.
 *
 * Field shapes match the launcher's original hand-written `SalesforceCampaign` interface + the launcher's observed
 * ?? coalesces. `id` and `name` are required because they write to NOT NULL
 * columns (prisma/schema/migrations/20260317_launcher_sfdc_tables).
 */
export const salesforceCampaignSchema = z.object({
  id: z.string(),
  // `name` stays required: `launcher_sfdc_campaigns.name` is TEXT NOT NULL and
  // SFDC `Campaign.Name` is a required field Middleman always sends.
  name: z.string(),
  type: z.string().nullish(),
  subtype: z.string().nullish(),
  startDate: z.string().nullish(),
  endDate: z.string().nullish(),
  status: z.string().nullish(),
  description: z.string().nullish(),
  city: z.string().nullish(),
  state: z.string().nullish(),
  capacity: z.number().nullish(),
  budgetedCost: z.number().nullish(),
  memberCount: z.number().default(0),
  eventBriefUrl: z.string().nullish(),
  registrationGoal: z.number().nullish(),
  pipelineGoal: z.number().nullish(),
  arrGoal: z.number().nullish(),
  targetPersona: z.string().nullish(),
  goal: z.string().nullish(),
  partner: z.string().nullish(),
  partnerActivationItems: z.string().nullish(),
  inviteSubject: z.string().nullish(),
  inviteBody: z.string().nullish(),
});
export type SalesforceCampaign = z.infer<typeof salesforceCampaignSchema>;

// ─── CampaignMember (+ nested sub-schemas) ───────────────────────────────────

/**
 * Nested account sub-schema on a CampaignMember's contact.
 * Every field is .nullish() — Middleman omits unset SFDC fields.
 * .passthrough() preserves forward-compatible unknown fields.
 */
const salesforceAccountSchema = z
  .object({
    name: z.string().nullish(),
    ownerId: z.string().nullish(),
    salesTier: z.string().nullish(),
    accountFit: z.string().nullish(),
    namedAccountStatus: z.string().nullish(),
    potentialContractValue: z.number().nullish(),
    arrActiveSubscriptions: z.number().nullish(),
    suppressed: z.boolean().nullish(),
    suppressionReasons: z.string().nullish(),
    isCompetitor: z.boolean().nullish(),
    isAgency: z.boolean().nullish(),
    accountType: z.string().nullish(),
    numberOfOpenOpps: z.number().nullish(),
    growthStrategistId: z.string().nullish(),
    clayDrId: z.string().nullish(),
    lastGtmeMeetingDate: z.string().nullish(),
    lastGsMeetingDate: z.string().nullish(),
    lastClayDrMeetingDate: z.string().nullish(),
    productTier: z.string().nullish(),
    planInterval: z.string().nullish(),
    arrOpportunity: z.number().nullish(),
    upcomingRenewalCloseDate: z.string().nullish(),
    lastOpportunityStage: z.string().nullish(),
    partnerType: z.string().nullish(),
    openPipeline: z.number().nullish(),
    companySegment: z.string().nullish(),
  })
  .passthrough();

/**
 * Nested contact sub-schema on a CampaignMember.
 * Every field is .nullish(). .passthrough() for forward compat.
 */
const salesforceContactSchema = z
  .object({
    name: z.string().nullish(),
    firstName: z.string().nullish(),
    lastName: z.string().nullish(),
    email: z.string().nullish(),
    title: z.string().nullish(),
    accountId: z.string().nullish(),
    mailingCity: z.string().nullish(),
    mailingState: z.string().nullish(),
    contactStage: z.string().nullish(),
    persona: z.string().nullish(),
    jobLevel: z.string().nullish(),
    fitScore: z.number().nullish(),
    noLongerAtCompany: z.boolean().nullish(),
    suppressed: z.boolean().nullish(),
    suppressionReasons: z.string().nullish(),
    hasOptedOutOfEmail: z.boolean().nullish(),
    doNotCall: z.boolean().nullish(),
    linkedin: z.string().nullish(),
    ownerId: z.string().nullish(),
    ownerEmail: z.string().nullish(),
    ownerFullName: z.string().nullish(),
    ownerFirstName: z.string().nullish(),
    ownerRole: z.string().nullish(),
    ownerMeetingLink: z.string().nullish(),
    gtmeFirstName: z.string().nullish(),
    lastGtmeMeetingDate: z.string().nullish(),
    growthStrategistEmail: z.string().nullish(),
    growthStrategistFullName: z.string().nullish(),
    growthStrategistFirstName: z.string().nullish(),
    growthStrategistMeetingLink: z.string().nullish(),
    lastGsMeetingDate: z.string().nullish(),
    clayDrEmail: z.string().nullish(),
    clayDrFirstName: z.string().nullish(),
    clayDrMeetingLink: z.string().nullish(),
    lastClayDrMeetingDate: z.string().nullish(),
    lastInboundEmailDate: z.string().nullish(),
    lastInboundFormSubmissionDate: z.string().nullish(),
    lastContactUsFormSubmissionDate: z.string().nullish(),
    lastClayDrEmailDate: z.string().nullish(),
    qualificationStatus: z.string().nullish(),
    lastGtmeEmailDate: z.string().nullish(),
    lastGsEmailDate: z.string().nullish(),
    personaTier: z.string().nullish(),
    account: salesforceAccountSchema.nullish(),
  })
  .passthrough();

/**
 * Nested lead sub-schema on a CampaignMember.
 * Every field is .nullish(). .passthrough() for forward compat.
 */
const salesforceLeadSchema = z
  .object({
    name: z.string().nullish(),
    firstName: z.string().nullish(),
    lastName: z.string().nullish(),
    email: z.string().nullish(),
    title: z.string().nullish(),
    company: z.string().nullish(),
    ownerEmail: z.string().nullish(),
  })
  .passthrough();

/**
 * Deep schema for a single Salesforce CampaignMember returned by
 * POST /salesforce/campaign-members (envelope key: `members`).
 *
 * `id` is required — it is the primary key for the upsert.
 * All other fields are .nullish() because Middleman omits unset SFDC fields.
 * .passthrough() preserves unknown top-level fields for forward compat.
 */
export const salesforceCampaignMemberSchema = z
  .object({
    id: z.string(),
    campaignId: z.string().nullish(),
    contactId: z.string().nullish(),
    leadId: z.string().nullish(),
    status: z.string().nullish(),
    hasResponded: z.boolean().nullish(),
    createdDate: z.string().nullish(),
    notes: z.string().nullish(),
    metWithNotes: z.string().nullish(),
    primaryUserMetWith: z.string().nullish(),
    secondaryUserMetWith: z.string().nullish(),
    tertiaryUserMetWith: z.string().nullish(),
    lastFollowUpDate: z.string().nullish(),
    isRequalified: z.boolean().nullish(),
    requalificationReason: z.string().nullish(),
    contact: salesforceContactSchema.nullish(),
    lead: salesforceLeadSchema.nullish(),
  })
  .passthrough();
export type SalesforceCampaignMember = z.infer<typeof salesforceCampaignMemberSchema>;

// ─── CampaignMemberStatus ────────────────────────────────────────────────────

/**
 * Schema for a single CampaignMember status option returned by
 * POST /salesforce/campaign-member-statuses (envelope key: `statuses`).
 *
 * Moved here from types.ts to co-locate with the other read schemas.
 * The .passthrough() preserves any future Middleman fields added to this type.
 */
export const SalesforceCampaignMemberStatusSchema = z
  .object({
    id: z.string(),
    label: z.string(),
    isDefault: z.boolean(),
    hasResponded: z.boolean(),
    sortOrder: z.number(),
  })
  .passthrough();
export type SalesforceCampaignMemberStatus = z.infer<typeof SalesforceCampaignMemberStatusSchema>;

// ─── Response Envelopes ───────────────────────────────────────────────────────
//
// Canonical envelope schemas wrapping the item arrays above.
// Exported so both client.ts and the launcher-*-sync.ts DBOS files import the
// SAME schema object — one definition, zero drift.
//
// Design invariants:
//   - campaigns/members/statuses arrays are all REQUIRED (no .default([])):
//     an absent key on a 200 response is a protocol error and must throw,
//     not silently coerce to an empty-array success. The *type* stays a
//     non-nullable array either way — no downstream null-checks are
//     introduced by this — only absence-tolerance is removed. A legitimately
//     empty result set still parses fine as `[]`; only a missing key throws.
//   - .passthrough() on all three: forward-compat — extra top-level keys from
//     Middleman survive the parse without causing validation failures.

/**
 * Envelope for POST /salesforce/campaigns.
 * Outer key: `campaigns`. Missing key is a protocol error.
 */
export const ReadCampaignsResponseSchema = z
  .object({
    campaigns: z.array(salesforceCampaignSchema),
  })
  .passthrough();

/**
 * Envelope for POST /salesforce/campaign-members.
 * Outer key: `members`. Missing key is a protocol error.
 */
export const ReadCampaignMembersResponseSchema = z
  .object({
    members: z.array(salesforceCampaignMemberSchema),
  })
  .passthrough();

/**
 * Envelope for POST /salesforce/campaign-member-statuses.
 * Outer key: `statuses`. Missing key is a protocol error.
 */
export const ReadCampaignMemberStatusesResponseSchema = z
  .object({
    statuses: z.array(SalesforceCampaignMemberStatusSchema),
  })
  .passthrough();

// ─── Open Signal Actions (Action View Slack notifier) ────────────────────────
//
// Narrowed view of POST /salesforce/signal-actions, for the 15-minute Action
// View notifier cron (core-owned). The mini-app's own full-fat schema lives at
// `mini-apps/action-view/types.ts:79-150` (`SignalActionSchema`) — core must
// not import `mini-apps/**` (lane rule, `mini-apps/CLAUDE.md`), and the cron
// needs ~12 of that schema's ~40 fields, so this is a deliberate narrow
// re-declaration, not a copy. `z.object` strips undeclared keys, which is what
// keeps the ~8.5 MB org-wide response from being retained wholesale per tick.
//
// Everything except `id` is `.nullish()` on purpose: this response is parsed
// with `safeParse` + empty-list fallback, so an over-strict field would take
// the whole tick to zero rather than degrade one field. `id` is the only field
// the pipeline cannot function without (it is the claim-table PK half).

/**
 * One row of POST /salesforce/signal-actions, narrowed + flattened for the
 * notifier cron.
 *
 * Wire-shape notes (verified against `mini-apps/action-view/types.ts:79-150`):
 *   - `accountName` / `contactName` are NOT top-level on the wire; they live at
 *     `contact.account.name` / `contact.name`. The transform flattens them.
 *   - `gongFlow` is present on the wire EXACTLY when Middleman could traverse
 *     the `Signal_Action__c.Gong_Engage_Flow__c` lookup
 *     (`mini-apps/action-view/docs/middleware-gong-fields-spec.md:24`), so its
 *     presence — not any id field — is the notifier's linkage signal. It is
 *     therefore passed through as-is: NOT synthesised from
 *     `gongEngageFlowInstanceId`, because 18 open SAs in prod (2026-08-13)
 *     carry an instance id with an unresolved lookup (the known Flow-2
 *     backfill gap, `mini-apps/action-view/docs/sfdc-object-schema.md:152`)
 *     and a synthesised status-less object would misread as `unverified`
 *     instead of `no-linkage`.
 *   - `signalActionSubtype` / `playType` are the other two thirds of a
 *     notification rule's identity key (`{type, subtype, playType}` — the same
 *     triple the Priority Ranker groups by). They were originally stripped
 *     here to keep the narrowing tight; they are declared again because a
 *     stripped key is invisible (`z.object` drops undeclared keys silently)
 *     and would make every rule with a non-empty subtype match nothing.
 *   - `gongFlow.stepType` and `gongFlow.status` are both SFDC-sourced here (via
 *     `Gong_Engage_Flow__r`). `status` is the `Gong__Engage_Flow_Status__c`
 *     picklist ("In Progress" / "Paused" / "Finished (…)"), NOT the live Gong
 *     API's `Running`/`Paused`/`Pending` vocabulary. The live
 *     `/v2/flows/prospects` call carries no step data either — do not expect
 *     stepType from there (`merge-gong-enrichment.ts:33-67`).
 */
export const openSignalActionSchema = z
  .object({
    id: z.string(),
    signalActionType: z.string().nullish(),
    signalActionSubtype: z.string().nullish(),
    playType: z.string().nullish(),
    status: z.string().nullish(),
    assigneeEmail: z.string().nullish(),
    assigneeRole: z.string().nullish(),
    contactId: z.string().nullish(),
    accountId: z.string().nullish(),
    // `createdDate` is deliberately NOT declared here. It WAS a phantom field
    // on this endpoint through 2026-08-20 (Middleman's `SignalActionDto` never
    // sent `CreatedDate`, so every row silently resolved it to `null`), but
    // Middleman PR #304 added it to the wire after that — this schema simply
    // never re-declared it, because its last reader (`isUnverifiedRetryExpired`
    // in `pipeline.ts`) had already moved to `surfacedDate` before the field
    // showed up for real. Declaring it here would be safe today, but there is
    // no reader left that needs it on THIS narrowed endpoint; the live,
    // full-fat `liveSignalActionSchema` below is where `createdDate` is
    // actually read (refresh-on-read's mirror upsert). `surfacedDate`
    // (`Surfaced_Date__c`) IS genuinely on the wire — verified against
    // `mini-apps/action-view/types.ts:129`'s full-fat schema, which reads the
    // same endpoint — and is the real-world stand-in for "when did this
    // become notify-worthy".
    surfacedDate: z.string().nullish(),
    contact: z
      .object({
        name: z.string().nullish(),
        account: z.object({ name: z.string().nullish() }).nullish(),
      })
      .nullish(),
    gongFlow: z
      .object({
        name: z.string().nullish(),
        stepNumber: z.number().nullish(),
        stepType: z.string().nullish(),
        status: z.string().nullish(),
      })
      .nullish(),
  })
  .transform((row) => ({
    id: row.id,
    signalActionType: row.signalActionType ?? null,
    signalActionSubtype: row.signalActionSubtype ?? null,
    playType: row.playType ?? null,
    status: row.status ?? null,
    assigneeEmail: row.assigneeEmail ?? null,
    assigneeRole: row.assigneeRole ?? null,
    accountName: row.contact?.account?.name ?? null,
    contactName: row.contact?.name ?? null,
    contactId: row.contactId ?? null,
    accountId: row.accountId ?? null,
    surfacedDate: row.surfacedDate ?? null,
    gongFlow: row.gongFlow
      ? {
          stepType: row.gongFlow.stepType ?? null,
          stepNumber: row.gongFlow.stepNumber ?? null,
          name: row.gongFlow.name ?? null,
          status: row.gongFlow.status ?? null,
        }
      : null,
  }));

/** A narrowed open Signal Action row as the notifier cron consumes it. */
export type SalesforceOpenSignalAction = z.infer<typeof openSignalActionSchema>;

/**
 * Envelope for POST /salesforce/signal-actions.
 * Outer key: `signalActions` (required — a missing key is a protocol error and
 * must trip the caller's empty-result fallback, not silently read as zero
 * open actions). `totalRecords` is on the wire but the cron does not use it,
 * so it is not declared.
 */
export const ReadOpenSignalActionsResponseSchema = z
  .object({
    signalActions: z.array(openSignalActionSchema),
  })
  .passthrough();

// ─── Live (full-fat) Signal Action — refresh-on-read ───────────────────────

/**
 * `gongFlow` on the full-fat wire row. Derived from the mini-app's
 * `GongFlowSchema` (`mini-apps/action-view/types.ts:89-118`), cross-checked
 * against terra-middleware's `src/salesforce/dto/signal-action.dto.ts`
 * (the authoritative superset that added `dueDate` / `syncedAt` in PR #215).
 * Every field `.nullish()` — this is the "declare it or Zod silently strips
 * it" endpoint (see `openSignalActionSchema`'s doc above), and refresh-on-read
 * must survive a Middleman deploy that adds or omits a key rather than
 * rejecting the whole row over one unrecognised field.
 */
const liveGongFlowSchema = z.object({
  name: z.string().nullish(),
  stepNumber: z.number().nullish(),
  stepType: z.string().nullish(),
  stepSubject: z.string().nullish(),
  status: z.string().nullish(),
  lastStepCompletedDate: z.string().nullish(),
  // `Gong__Current_Flow_Task_Due_Date__c` — see the mini-app's own doc on
  // `GongFlowSchema.dueDate` for why this is a DATE, not a datetime.
  dueDate: z.string().nullish(),
  syncedAt: z.string().nullish(),
});

/**
 * `contact` on the full-fat wire row, narrowed to the fields this arm's
 * current readers need (the id-flattening pair `name` / `account.name`,
 * same depth `openSignalActionSchema` already declares). Derived from the
 * mini-app's `ContactSchema` (`mini-apps/action-view/types.ts:53-87`) — not
 * the full ~30-field contact record, which no Signal-Action reader in this
 * codebase consumes today; widen this if/when one does.
 */
const liveSignalActionContactSchema = z.object({
  name: z.string().nullish(),
  account: z.object({ name: z.string().nullish() }).nullish(),
});

/**
 * Full-fat row of POST /salesforce/signal-actions, for refresh-on-read (U3).
 * Unlike `openSignalActionSchema` (narrowed to ~12 fields for the notifier
 * cron), this declares every field of the mini-app's `SignalActionSchema`
 * (`mini-apps/action-view/types.ts:120-155` — cross-checked, not imported:
 * core must not import from `mini-apps/`, D13) PLUS the fields terra-
 * middleware's DTO (`src/salesforce/dto/signal-action.dto.ts`) adds on top of
 * that mini-app shape: `createdDate`, `lastModifiedDate`, `isDeleted`,
 * `gongEngageFlowLookupId` (the `Gong_Engage_Flow__c` lookup id — distinct
 * from `gongEngageFlowId`, the external-text `Gong_Engage_Flow_ID__c`; do not
 * collapse the two, same as `SalesforceSignalActionRowSchema`).
 *
 * `.nullish()` everywhere but `id`: `refreshSignalActionsForRep` (U3, next
 * bead) skips the WHOLE refresh on a malformed row rather than upserting a
 * partial one, so a field this permissive cannot itself cause that — only a
 * genuinely wrong `id` type can.
 *
 * `lastModifiedDate` STAYS `.nullish()` on purpose (bead .16, Bug 3), even
 * though it is the column `SIGNAL_ACTION_UPSERT_SPEC.monotonicOn` orders
 * writes by. Requiring it here was the considered alternative and was
 * rejected: the hourly SOQL leg tolerates a missing `LastModifiedDate`
 * (`SalesforceSignalActionRowSchema`'s `cell.optional()` → `coerceDateTime` →
 * null), so tightening only this schema would have left the two writers with
 * different contracts for the same column — and because the refresh path is
 * all-or-nothing, ONE untimestamped row would skip the whole rep's refresh.
 * The guard in `buildUpsertSql` is NULL-safe on both sides instead, and
 * `refreshSignalActionsForRep` warns with the count of such rows so a
 * Middleman regression here is loud rather than invisible.
 *
 * This is intentionally NOT `.transform()`ed:
 * `mapLiveSignalActionToMirror` (U3) reads fields directly off the parsed
 * shape, one field at a time, so there is exactly one place (the mapper) that
 * decides which columns the mirror table actually gets.
 */
export const liveSignalActionSchema = z.object({
  id: z.string(),
  name: z.string().nullish(),
  status: z.string().nullish(),
  signalActionType: z.string().nullish(),
  signalActionSubtype: z.string().nullish(),
  playType: z.string().nullish(),
  source: z.string().nullish(),
  priorityStamp: z.string().nullish(),
  surfacedDate: z.string().nullish(),
  daysOpen: z.number().nullish(),
  expirationDate: z.string().nullish(),
  lastActionDate: z.string().nullish(),
  archiveReason: z.string().nullish(),
  closedSource: z.string().nullish(),
  closedDate: z.string().nullish(),
  gongEngageFlowId: z.string().nullish(),
  gongEngageFlowInstanceId: z.string().nullish(),
  gongEngageFlowLookupId: z.string().nullish(),
  gongFlow: liveGongFlowSchema.nullish(),
  // The raw `Gong_Engage_Flow__r` record plus `Id` (= `Gong_Engage_Flow__c`), in the hourly
  // leg's own row shape, so refresh-on-read maps it with `mapGongEngageFlowRow`. Loose: this
  // leaf file may import only zod, so the field-level typing lives in that mapper's schema.
  gongFlowRow: z.looseObject({ Id: z.string() }).nullish(),
  campaignMemberId: z.string().nullish(),
  campaignMemberStatus: z.string().nullish(),
  touchpointId: z.string().nullish(),
  eventAndSignalId: z.string().nullish(),
  assigneeId: z.string().nullish(),
  assigneeName: z.string().nullish(),
  assigneeEmail: z.string().nullish(),
  assigneeRole: z.string().nullish(),
  assigneeManager: z.string().nullish(),
  campaignId: z.string().nullish(),
  campaignName: z.string().nullish(),
  contactId: z.string().nullish(),
  contact: liveSignalActionContactSchema.nullish(),
  accountId: z.string().nullish(),
  campaignDescription: z.string().nullish(),
  campaignMemberNotes: z.string().nullish(),
  createdDate: z.string().nullish(),
  lastModifiedDate: z.string().nullish(),
  isDeleted: z.boolean().nullish(),
});

/** A full-fat Signal Action row as refresh-on-read (U3) consumes it. */
export type LiveSignalAction = z.infer<typeof liveSignalActionSchema>;

/**
 * Envelope for `POST /salesforce/signal-actions` scoped by `assigneeEmails`
 * (refresh-on-read's shape of the same endpoint `ReadOpenSignalActionsResponseSchema`
 * reads org-wide). Same outer-key contract: `signalActions` required, a
 * missing key is a protocol error the caller must treat as a failure, never
 * as zero rows — `fetchSignalActionsForAssignees` throws rather than
 * returning an empty array precisely because a silent empty result here would
 * let refresh-on-read report success while writing nothing.
 */
export const ReadSignalActionsForAssigneesResponseSchema = z
  .object({
    signalActions: z.array(liveSignalActionSchema),
  })
  .passthrough();

// ─── Ranker stats — Configuration → Ranker (plan 005, U2) ───────────────────

/**
 * `contact` on the wire, widened for the Ranker admin's stats/records routes
 * (`GET /api/v4/admin/priority-ranking/stats`,
 * `.../stats/records` — plan `plans/2026-09-24-005-signals-ranker-admin-plan.md`,
 * Decision 7) beyond what `liveSignalActionContactSchema` declares. Those
 * routes group and filter open/closed Signal Actions by fit score, persona,
 * sales tier, account fit, and industry — fields `liveSignalActionContactSchema`
 * deliberately does not carry.
 *
 * This is a SEPARATE, wider schema rather than an edit to
 * `liveSignalActionContactSchema` itself: that schema feeds
 * `mapLiveSignalActionToMirror` (refresh-on-read, U3), which reads its parsed
 * shape field-by-field to decide what the mirror table gets column-by-column
 * (see `liveSignalActionSchema`'s doc above). Widening the shared contact
 * schema would silently hand that mapper new columns it was never reviewed
 * against — the Ranker's read-only stats path has no such mapper and no such
 * risk, so it gets its own schema instead. All fields `.nullish()`, same
 * degrade-one-field-not-the-whole-tick rationale as `openSignalActionSchema`.
 * Cite `mini-apps/action-view/types.ts:53-80 ContactSchema` /
 * `types.ts:15-39 AccountSchema` for the source field names.
 */
export const rankerStatsContactSchema = z.object({
  name: z.string().nullish(),
  title: z.string().nullish(),
  email: z.string().nullish(),
  persona: z.string().nullish(),
  contactStage: z.string().nullish(),
  fitScore: z.number().nullish(),
  account: z
    .object({
      name: z.string().nullish(),
      salesTier: z.string().nullish(),
      accountFit: z.string().nullish(),
      industry: z.string().nullish(),
    })
    .nullish(),
});
export type RankerStatsContact = z.infer<typeof rankerStatsContactSchema>;

/**
 * `liveSignalActionSchema` with `contact` replaced by the wider
 * `rankerStatsContactSchema`. `liveSignalActionSchema` is a plain `z.object`
 * (no `.transform()`/effects wrapper — verified above), so `.extend()` is
 * safe here: it replaces the `contact` key only, every other field is
 * untouched.
 */
export const rankerStatsSignalActionSchema = liveSignalActionSchema.extend({
  contact: rankerStatsContactSchema.nullish(),
});
export type RankerStatsSignalAction = z.infer<typeof rankerStatsSignalActionSchema>;

/**
 * Envelope for the Ranker stats/records routes' Middleman fetch (same
 * endpoint as `ReadSignalActionsForAssignees`/`ReadOpenSignalActions`, wider
 * per-row contact). `.passthrough()` + required `signalActions`, same
 * protocol-error contract as the other envelopes on this endpoint.
 */
export const ReadRankerStatsSignalActionsResponseSchema = z
  .object({
    signalActions: z.array(rankerStatsSignalActionSchema),
  })
  .passthrough();
